<template>
  <div>
    <!-- Profile icon -->
    <button
      title="Click to open profile menu"
      class="relative flex max-w-xs items-center rounded-full bg-white text-sm
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      @click.stop="toggleMenu">
      <span class="sr-only">Profile menu</span>
      <img
        class="size-8 rounded-full object-cover "
        :src="$page.props.user.profile_photo_url"
        alt="">
      <span
        v-if="$page.props.team_invitations.length > 0"
        class="absolute bottom-0 right-0 block size-2 rounded-full bg-red-400 ring-2 ring-white" />
    </button>

    <!-- Profile menu -->
    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div
        v-if="showMenu"
        v-click-outside="(e) => toggleMenu(e, false)"
        class="absolute right-2 z-50 mt-2 w-[500px] origin-top-right divide-y
            divide-gray-200 overflow-hidden rounded-md bg-white shadow-xl shadow-gray-400 ring-1 ring-black/5
            focus:outline-none">
        <div class="flex flex-row">
          <!-- Left column -->
          <div class="flex basis-1/2 flex-col border-r bg-stone-100">
            <!-- Teams -->
            <p class="p-2 px-4 font-medium">
              Available teams
            </p>
            <ul
              v-for="org in sortedTeams"
              :key="org.id">
              <li
                class="relative cursor-pointer select-none text-sm text-gray-900"
                title="Click to switch to this team"
                @click="switchToTeam(org)">
                <div
                  class="flex flex-col bg-stone-100 px-2 py-1 hover:bg-stone-300"
                  :class="{'bg-stone-200': org.id === $page.props.user.current_team.id}">
                  <div class="flex flex-row items-center">
                    <div class="rounded-full p-1.5">
                      <img
                        class="size-8 rounded-full object-cover "
                        :src="$page.props.user.profile_photo_url"
                        alt="">
                    </div>
                    <div class="ml-2">
                      <p :class="org.id === $page.props.user.current_team.id ? 'font-semibold' : 'font-normal, cursor-pointer'">
                        {{ org.personal_team ? 'Personal projects' : org.name }}
                      </p>
                    </div>
                    <span
                      v-if="org.id === $page.props.user.current_team.id"
                      class="ml-auto block text-indigo-700">
                      <CheckIcon
                        class="size-5"
                        aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </li>
            </ul>

            <!-- Team invitations -->
            <ul
              v-if="$page.props.team_invitations.length > 0"
              class="mt-12 flex flex-col bg-yellow-50 px-4 py-2">
              <p class="mb-1 mt-2 text-center text-sm font-medium">
                You have been invited to join
              </p>
              <li
                v-for="invitation in $page.props.team_invitations"
                :key="invitation.id"
                class="relative flex select-none flex-col items-center space-y-4 border-t border-stone-200
                   py-4 text-sm text-gray-900 first-of-type:mt-0 first-of-type:border-t-0">
                <span class="flex items-center">
                  <img
                    class="mr-2 size-6 rounded-full object-cover"
                    :src="invitation.team.photo_url"
                    :alt="invitation.team.name">
                  {{ invitation.team.name }}
                </span>
                <span class="flex flex-row space-x-2 text-xs">
                  <Link
                    class="cursor-pointer rounded-full border border-green-600 px-4 py-1 text-center
                      font-semibold text-green-600 hover:bg-green-600 hover:text-white"
                    @click.prevent="handleAcceptLinkClick(invitation.acceptLink)">
                    Accept</Link>
                  <Link
                    class="cursor-pointer rounded-full border border-red-600 px-4 py-1 text-center
                      font-semibold text-red-600 hover:bg-red-600 hover:text-white"
                    :href="invitation.declineLink"
                    method="delete">
                    Decline</Link>
                </span>
              </li>
            </ul>

            <!-- Bottom items -->
            <ul class="mt-auto border-t border-stone-200">
              <li class="relative flex cursor-default  select-none flex-row text-sm text-gray-900">
                <Link
                  v-if="$page.props.user.current_team"
                  class="flex w-full flex-row p-4"
                  :href="route('teams.create', {team: $page.props.user.current_team,})"
                  @click="toggleMenu">
                  <PlusIcon
                    class="size-5 text-gray-400"
                    aria-hidden="true" />
                  <p class="w-5/6 pl-2 text-gray-700 hover:underline">
                    Create new team
                  </p>
                </Link>
              </li>
            </ul>
          </div>

          <!-- Right column -->
          <div class="basis-1/2">
            <div class="flex-col divide-y divide-gray-200">
              <div class="flex flex-col items-center justify-center p-4">
                <img
                  class="size-14 rounded-full object-cover "
                  :src="$page.props.user.profile_photo_url"
                  alt="">
                <p class="mt-2 font-medium text-gray-700">
                  {{ $page.props.user.name }}
                </p>
              </div>
              <ul class="flex flex-col divide-y divide-gray-200">
                <li class="relative flex cursor-default select-none flex-row text-sm text-gray-900">
                  <Link
                    class="flex w-full cursor-pointer flex-row p-4"
                    :href="route('profile.show')"
                    @click="toggleMenu">
                    <UserCircleIcon
                      class="size-5 text-gray-400"
                      aria-hidden="true" />
                    <p class="w-5/6 pl-2 text-gray-700 hover:underline">
                      Profile settings
                    </p>
                  </Link>
                </li>
                <li
                  v-if="can('edit brands')"
                  class="relative flex cursor-default select-none flex-row text-sm text-gray-900">
                  <div
                    class="flex w-full cursor-pointer flex-row p-4"
                    @click="goToTeamSettings()">
                    <Cog6ToothIcon
                      class="size-5 text-gray-400"
                      aria-hidden="true" />
                    <p class="w-5/6 pl-2 text-gray-700 hover:underline">
                      Team settings
                    </p>
                  </div>
                </li>
                <li class="relative flex cursor-default  select-none flex-row text-sm text-gray-900">
                  <form
                    method="POST"
                    class="w-full"
                    @submit.prevent="logout">
                    <button
                      type="submit"
                      class="flex w-full flex-row p-4"
                      role="menuitem">
                      <ArrowRightStartOnRectangleIcon
                        class="size-5 text-gray-400"
                        aria-hidden="true" />
                      <span
                        class="w-5/6 cursor-pointer pl-2 text-left text-gray-700 hover:underline">Log out</span>
                    </button>
                  </form>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
/* external */
import { router as Inertia } from '@inertiajs/core';
import {
  Link, usePage,
} from '@inertiajs/vue3';
import {
  computed, ref, watch,
} from 'vue';
import {
  UserCircleIcon, Cog6ToothIcon,
} from '@heroicons/vue/24/outline';
import {
  ArrowRightStartOnRectangleIcon, CheckIcon, PlusIcon,
} from '@heroicons/vue/24/solid';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const page = usePage();
const emit = defineEmits(['update:show']);

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showMenu = ref(props.show);

// Define `user` as a computed property with a fallback to prevent undefined access
const user = computed(() => page.props.user || null);

const sortedTeams = computed(() => {
  if (!user.value) {
    return [];
  }

  return [...user.value.all_teams].sort((a, b) => {
    if (a.id === user.value.current_team.id) {
      return -1;
    }
    if (b.id === user.value.current_team.id) {
      return 1;
    }

    return 0;
  });
});

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Toggle profile menu
 * @param {object} event - Event object
 * @param {boolean} state - true to show, false to hide
 */
const toggleMenu = (event, state = !showMenu.value) => {
  showMenu.value = state;
  emit('update:show', state);
};

/**
 * Switch to team
 * @param {object} team - team object
 */
function switchToTeam(team) {
  Inertia.put(route('current-team.update'), { team_id: team.id }, {
    preserveState: true,
    onSuccess: () => {
      Inertia.reload({ only: ['user', 'teams'] });
      toggleMenu(null, false);
    },
  });
}

/**
 * Go to team settings
 */
function goToTeamSettings() {
  const team = sortedTeams.value.find((t) => t.id === user.value.current_team.id);
  if (team) {
    Inertia.get(route('teams.settings', { team: team.id }));
    toggleMenu(null, false);
  }
}

/**
 * Handle accept link click
 * @param {string} url - The URL to visit
 */
function handleAcceptLinkClick(url) {
  Inertia.get(url, {}, {
    onSuccess: (response) => {
      const newTeamId = response.props.new_team_id;
      if (newTeamId) {
        switchToTeam({ id: newTeamId });
      } else {
        Inertia.reload({ only: ['user', 'teams'] });
      }
    },
  });
}

/**
 * Logout
 */
function logout() {
  Inertia.post(route('logout'));
  toggleMenu(null, false);
}

/* *********************************************************************
 * Component lifecycle functions
 * ****************************************************************** */

// Watch for changes in `props.show` to toggle the menu
watch(() => props.show, (newVal) => {
  showMenu.value = newVal;
});
</script>
